import {
    flagIsEmpty,
    clearFilter,
    tomeIsEmpty,
    unreadIsEmpty,
    attachmentsIsEmpty,
    attachmentsIsEmptyDetailMessage,
    noSearchResultsDetailMessage,
    noSearchResultsInArchiveMailboxResetFolderScopeLink,
    noSearchResultsResetFolderScopeLink,
    consumerGroupFolderAccessDenied,
    groupFolderAccessDenied,
    accessDeniedText,
    mailListErrorLinkMessage,
    categoryFolderEmpty,
    unexpectedErrorMainMessage,
    unexpectedErrorDetailMessage,
    inboxEmptyStateMessageText,
    otherEmptyStateMessageText,
    inboxEmptyStateDetailMessageText,
    mentionIsEmpty,
    nothingInDistinguishedFolder,
    junkEmptyDisplayName,
    deletedEmptyDisplayName,
    draftsEmptyDetailMessage,
    sentEmptyDisplayName,
    archiveEmptyDetailMessage,
    filterEmptyDetailMessage,
    sentEmptyDetailMessage,
    spotlightFilterMainMessage,
} from '../strings.locstring.json';
import { loadingWithThreeDots as loadingFolder } from 'owa-locstrings/lib/strings/loading.locstring.json';
import { mailListErrorMessage } from 'owa-locstrings/lib/strings/maillisterrormessage.locstring.json';
import {
    folderIsEmpty,
    emptyFolderDetailMessage,
} from 'owa-locstrings/lib/strings/folderisempty.locstring.json';
import { searching } from 'owa-locstrings/lib/strings/searching.locstring.json';
import { noSearchResults } from 'owa-locstrings/lib/strings/nosearchresults.locstring.json';
import { noMoreSearchResults } from 'owa-locstrings/lib/strings/nomoresearchresults.locstring.json';
import loc, { format } from 'owa-localize';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import {
    ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
    PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
    PRIMARY_DUMPSTER_DISTINGUISHED_ID,
    ARCHIVE_DUMPSTER_DISTINGUISHED_ID,
} from 'owa-folders-constants';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import { clearSearchScope } from 'owa-mail-search';
import { getFolderIdForSelectedNode } from 'owa-mail-folder-forest-store';
import { getIsLightBaseTheme, getIsDarkBaseTheme } from 'owa-theme';
import { logVerboseUsage, logUsage } from 'owa-analytics';
import { SearchTableQuery } from 'owa-mail-list-search';
import { MailListViewState } from 'owa-mail-store/lib/store/schema/MailListViewState';
import * as trace from 'owa-trace';
import {
    getViewFilterForTable,
    LoadErrorStatus,
    TableQuery,
    TableView,
    listViewStore,
    TableQueryType,
    getFocusedFilterForTable,
    MailFolderTableQuery,
} from 'owa-mail-list-store';
import {
    SearchScopeKind,
    PrimaryMailboxSearchScope,
    ArchiveMailboxSearchScope,
} from 'owa-search-service';
import SearchScenarioId from 'owa-search/lib/store/schema/SearchScenarioId';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { isFeatureEnabled } from 'owa-feature-flags';
import { folderIsBeingEmptied } from 'owa-locstrings/lib/strings/folderisbeingemptied.locstring.json';
import { isBulkActionInState, BulkActionStateEnum } from 'owa-bulk-action-store';
import { EmptyStateProps } from 'owa-empty-state/lib/components/EmptyState';
import folderStore, { getEffectiveFolderDisplayName } from 'owa-folders';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { getIsMsHighContrast } from 'owa-high-contrast';
import { lazyClearSpotlightFilter } from 'owa-mail-filter-actions';

let EMPTY_INBOX_SVG;
let EMPTY_OTHER_SVG;
let EMPTY_FLAGGED_SVG;
let EMPTY_TOME_SVG;
let EMPTY_UNREAD_SVG;
let EMPTY_MENTIONS_SVG;
let EMPTY_ATTACHMENTS_SVG;
let EMPTY_SEARCH_SVG;
let EMPTY_SEARCH_ATTACHMENTS_SVG;
let EMPTY_SEARCH_CUSTOM_FOLDER_SVG;
let EMPTY_JUNK_SVG;
let EMPTY_DRAFT_SVG;
let EMPTY_DELETED_SVG;
let EMPTY_FOLDER_SVG;
let LOADING_ERROR_SVG;
let EMPTY_SENT_SVG;
let EMPTY_ARCHIVE_SVG;
let ACCESS_ERROR_SVG;

export interface GetMailListEmptyStateState {
    tableView: TableView;
}

/**
 * Get the empty list view display properties if user is in search mode
 * @param emptyStateProps the empty state display properties
 * @param tableQuery the table query
 * @param isLoading is currently loading search results
 */
function getEmptyListViewDisplayPropertiesForSearch(
    emptyStateProps: EmptyStateProps,
    tableQuery: TableQuery,
    isLoading: boolean,
    itemsPreviouslyLoaded: boolean
) {
    emptyStateProps.icon = EMPTY_SEARCH_SVG;

    if (isLoading) {
        emptyStateProps.mainMessage = loc(searching);
        emptyStateProps.detailMessage = null;
    } else if (itemsPreviouslyLoaded) {
        emptyStateProps.mainMessage = loc(noMoreSearchResults);
        emptyStateProps.detailMessage = null;
    } else {
        emptyStateProps.mainMessage = loc(noSearchResults);
        emptyStateProps.detailMessage = loc(noSearchResultsDetailMessage);
    }

    // Display the option to clean the search folder scope when the selected scope is not a root folder of any mailbox.
    const searchTableQuery = tableQuery as SearchTableQuery;
    const searchScopeKind = searchTableQuery.searchScope.kind;
    if (
        searchScopeKind == SearchScopeKind.PrimaryMailbox ||
        searchScopeKind == SearchScopeKind.ArchiveMailbox
    ) {
        const folderName = folderIdToName(
            (searchTableQuery.searchScope as PrimaryMailboxSearchScope | ArchiveMailboxSearchScope)
                .folderId
        );

        if (isFeatureEnabled('tri-emptyStateUpdates')) {
            if (searchTableQuery.includeAttachments) {
                emptyStateProps.icon = EMPTY_SEARCH_ATTACHMENTS_SVG;
            } else if (folderName !== 'msgfolderroot') {
                emptyStateProps.icon = EMPTY_SEARCH_CUSTOM_FOLDER_SVG;
            }
        }

        if (
            folderName != PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID &&
            folderName != ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID &&
            !isLoading &&
            !itemsPreviouslyLoaded
        ) {
            emptyStateProps.ctaMessage =
                searchScopeKind == SearchScopeKind.ArchiveMailbox
                    ? loc(noSearchResultsInArchiveMailboxResetFolderScopeLink)
                    : loc(noSearchResultsResetFolderScopeLink);
            emptyStateProps.onCtaClick = () => {
                logUsage('Search_SearchAllFoldersClicked', [SearchScenarioId.Mail]);
                clearSearchScope('EmptyListState', true /* shouldStartSearch */, searchScopeKind);
            };

            logUsage('Search_SearchAllFoldersRendered', [SearchScenarioId.Mail]);
        }
    }
}

/**
 * Get the empty state display properties
 * @param showSearchEmptyState whether to show empty state for search
 * @param clearFilterCommand the command that resets the view filter to default view filter
 * @param viewFilter the current view filter
 * @param focusedFilter the focused view filter
 * @param tableQuery the table query
 */
function getEmptyStateDisplayProperties(
    showSearchEmptyState: boolean,
    clearFilterCommand: () => void,
    viewFilter: ViewFilter,
    focusedFilter: FocusedViewFilter,
    tableQuery: TableQuery,
    itemsPreviouslyLoaded: boolean
): EmptyStateProps {
    const emptyStateProps: EmptyStateProps = { mainMessage: null, icon: null };

    if (showSearchEmptyState) {
        getEmptyListViewDisplayPropertiesForSearch(
            emptyStateProps,
            tableQuery,
            false,
            itemsPreviouslyLoaded
        );
    } else if ((tableQuery as MailFolderTableQuery).scenarioType === 'spotlight') {
        emptyStateProps.icon = EMPTY_FOLDER_SVG;
        emptyStateProps.mainMessage = loc(spotlightFilterMainMessage);
        emptyStateProps.detailMessage = loc(filterEmptyDetailMessage);
        emptyStateProps.ctaMessage = loc(clearFilter);
        emptyStateProps.onCtaClick = clearSpotlightFilter;
    } else if (viewFilter == 'All') {
        getEmptyListViewDisplayPropertiesForAllFilter(
            getFolderIdForSelectedNode(),
            emptyStateProps,
            focusedFilter
        );
    } else {
        getEmptyListViewDisplayPropertiesForFilters(
            viewFilter,
            clearFilterCommand,
            emptyStateProps
        );
    }

    return emptyStateProps;
}

/**
 * Calculate the mail list display properties based on the list view state
 * @param tableViewId tableView id
 * @param stateType the mail list view state type
 * @param clearFilterCommand the command that clears view filter
 * @param reloadCommand the command that reloads the selected list view
 */

export default function getDisplayPropertiesForEmptyList(
    tableViewId: string,
    stateType: MailListViewState,
    itemsPreviouslyLoaded: boolean,
    clearFilterCommand: () => void,
    reloadCommand: () => void,
    state: GetMailListEmptyStateState = { tableView: listViewStore.tableViews.get(tableViewId) }
) {
    let mailListState: EmptyStateProps = { icon: null, mainMessage: null };
    const tableView = state.tableView;
    const scenarioType = (tableView.tableQuery as SearchTableQuery).scenarioType;
    // For persona scenario we don't want to show search empty state but instead normal folder empty state
    const showSearchEmptyState =
        tableView.tableQuery.type === TableQueryType.Search &&
        scenarioType !== 'persona' &&
        scenarioType !== 'privateDistributionList';
    const isGroup = tableView.tableQuery.type == TableQueryType.Group;
    const viewFilter = getViewFilterForTable(tableView);
    const focusedFilter = getFocusedFilterForTable(tableView);
    const folderId = tableView.tableQuery.folderId;
    const isBulkAction =
        isBulkActionInState(folderId, BulkActionStateEnum.Running) ||
        isBulkActionInState(folderId, BulkActionStateEnum.Uninitialized);
    initializeSvgsForTheme();
    switch (stateType) {
        case MailListViewState.Error:
            mailListState = getDisplayPropertiesForError(
                tableView.loadErrorStatus,
                showSearchEmptyState,
                isGroup,
                reloadCommand
            );
            break;
        case MailListViewState.Loading:
            if (showSearchEmptyState) {
                getEmptyListViewDisplayPropertiesForSearch(
                    mailListState,
                    tableView.tableQuery,
                    true,
                    itemsPreviouslyLoaded
                );
            } else {
                mailListState.mainMessage = loc(loadingFolder);
                mailListState.icon = EMPTY_FOLDER_SVG;
            }
            break;
        case MailListViewState.Empty:
            mailListState = getEmptyStateDisplayProperties(
                showSearchEmptyState,
                clearFilterCommand,
                viewFilter,
                focusedFilter,
                tableView.tableQuery,
                itemsPreviouslyLoaded
            );
            break;
        default:
            trace.errorThatWillCauseAlert('Unrecognized empty state');
            break;
    }
    if (isBulkAction) {
        mailListState.mainMessage = loc(folderIsBeingEmptied);
        mailListState.detailMessage = null;
        mailListState.ctaMessage = null;
    }
    logVerboseUsage('TnS_MailListEmptyStateShownEvent', [
        stateType,
        showSearchEmptyState,
        viewFilter,
    ]);
    return mailListState;
}

/**
 * Get the display properties when list view is empty because an error occurred while loading
 * @param error LoadErrorStatus enum
 * @param showSearchError whether to show error for search
 * @param reloadCommand the command that reloads the selected list view
 */
function getDisplayPropertiesForError(
    error: LoadErrorStatus,
    showSearchError: boolean,
    isGroup: boolean,
    reloadCommand: () => void
): EmptyStateProps {
    switch (error) {
        case LoadErrorStatus.AccessDenied:
            return {
                mainMessage: isGroup
                    ? isConsumer()
                        ? loc(consumerGroupFolderAccessDenied)
                        : loc(groupFolderAccessDenied)
                    : loc(accessDeniedText),
                icon: ACCESS_ERROR_SVG,
            };
        case LoadErrorStatus.UnexpectedClientError:
            return {
                mainMessage: loc(unexpectedErrorMainMessage),
                icon: LOADING_ERROR_SVG,
                detailMessage: loc(unexpectedErrorDetailMessage),
            };
        default:
            return {
                mainMessage: loc(mailListErrorMessage),
                icon: LOADING_ERROR_SVG,
                ctaMessage: !showSearchError && loc(mailListErrorLinkMessage),
                onCtaClick: !showSearchError && reloadCommand,
            };
    }
}

/**
 * Get the display properties when list view is empty and user is in 'All' filter and in Focused or Other
 * @param focusedFilter focused or other
 * @param emptyStateProps the state which is used to calculate the mail list display properties
 */
export function getEmptyListViewDisplayPropertiesForAllFiltersFocusedOther(
    focusedViewFilter: FocusedViewFilter,
    emptyStateProps: EmptyStateProps
) {
    emptyStateProps.icon = EMPTY_INBOX_SVG;
    if (isFeatureEnabled('tri-emptyStateUpdates')) {
        emptyStateProps.icon =
            focusedViewFilter == FocusedViewFilter.Focused ? EMPTY_INBOX_SVG : EMPTY_OTHER_SVG;
    }
    emptyStateProps.detailMessage = loc(inboxEmptyStateDetailMessageText);
    emptyStateProps.mainMessage =
        focusedViewFilter == FocusedViewFilter.Focused
            ? loc(inboxEmptyStateMessageText)
            : loc(otherEmptyStateMessageText);
}

/**
 * Get the display properties when list view is empty and user is in 'All' filter and in a regular folder (not Focused or Other)
 * @param selectedFolderId selected folder id. Can be null if a persona was selected with no associated searchFolder
 * @param emptyStateProps the state which is used to calculate the mail list display properties
 */
function getEmptyListViewDisplayPropertiesForAllFiltersRegularFolders(
    selectedFolderId: string,
    emptyStateProps: EmptyStateProps
) {
    const folderName = selectedFolderId ? folderIdToName(selectedFolderId) : null;
    let mainMessage = loc(folderIsEmpty);
    let detailMessage = loc(emptyFolderDetailMessage);
    let icon = EMPTY_FOLDER_SVG;

    switch (folderName) {
        case 'inbox':
            mainMessage = loc(inboxEmptyStateMessageText);
            detailMessage = loc(inboxEmptyStateDetailMessageText);
            icon = EMPTY_INBOX_SVG;
            break;
        case 'junkemail':
            mainMessage = format(loc(nothingInDistinguishedFolder), loc(junkEmptyDisplayName));
            icon = EMPTY_JUNK_SVG;
            break;
        case 'deleteditems':
            mainMessage = format(loc(nothingInDistinguishedFolder), loc(deletedEmptyDisplayName));
            icon = EMPTY_DELETED_SVG;
            break;
        case 'drafts':
            mainMessage = format(
                loc(nothingInDistinguishedFolder),
                getEffectiveFolderDisplayName(folderStore.folderTable.get(selectedFolderId))
            );
            detailMessage = loc(draftsEmptyDetailMessage);
            icon = EMPTY_DRAFT_SVG;
            break;
        case 'sentitems':
            mainMessage = format(loc(nothingInDistinguishedFolder), loc(sentEmptyDisplayName));
            detailMessage = loc(sentEmptyDetailMessage);
            if (isFeatureEnabled('tri-emptyStateUpdates')) {
                icon = EMPTY_SENT_SVG;
            }
            break;
        case 'archive':
            const archiveFolderDisplayName = getEffectiveFolderDisplayName(
                folderStore.folderTable.get(selectedFolderId)
            );
            mainMessage = format(loc(nothingInDistinguishedFolder), archiveFolderDisplayName);
            detailMessage = format(loc(archiveEmptyDetailMessage), archiveFolderDisplayName);
            if (isFeatureEnabled('tri-emptyStateUpdates')) {
                icon = EMPTY_ARCHIVE_SVG;
            }
            break;
        case ARCHIVE_DUMPSTER_DISTINGUISHED_ID:
        case PRIMARY_DUMPSTER_DISTINGUISHED_ID:
            if (isFeatureEnabled('tri-emptyStateUpdates')) {
                icon = EMPTY_DELETED_SVG;
            }
            break;
    }

    emptyStateProps.mainMessage = mainMessage;
    emptyStateProps.detailMessage = detailMessage;
    emptyStateProps.icon = icon;
}

/**
 * Get the display properties when list view is empty and user is in 'All' filter
 * @param selectedFolderId selected folder id
 * @param emptyStateProps the state which is used to calculate the mail list display properties
 * @param focusedFilter the focused filter of the table
 */
export function getEmptyListViewDisplayPropertiesForAllFilter(
    selectedFolderId: string,
    emptyStateProps: EmptyStateProps,
    focusedFilter: FocusedViewFilter
) {
    if (focusedFilter !== FocusedViewFilter.None) {
        getEmptyListViewDisplayPropertiesForAllFiltersFocusedOther(focusedFilter, emptyStateProps);
    } else {
        getEmptyListViewDisplayPropertiesForAllFiltersRegularFolders(
            selectedFolderId,
            emptyStateProps
        );
    }
}

/**
 * Get the empty state properties based on the view filter
 * @param viewFilter the view filter
 * @param clearFilterCommand the clears filter command
 * @param emptyStateProps the state which is used to calculate the mail list display properties
 */
export function getEmptyListViewDisplayPropertiesForFilters(
    viewFilter: string,
    clearFilterCommand: () => void,
    emptyStateProps: EmptyStateProps
) {
    let mainMessage = loc(folderIsEmpty);
    let ctaMessage = loc(clearFilter);
    let onCtaClick = clearFilterCommand;
    let detailMessage = loc(filterEmptyDetailMessage);
    let icon = EMPTY_FOLDER_SVG;

    switch (viewFilter) {
        case 'Unread':
            mainMessage = loc(unreadIsEmpty);
            icon = EMPTY_UNREAD_SVG;
            break;
        case 'Flagged':
            mainMessage = loc(flagIsEmpty);
            icon = EMPTY_FLAGGED_SVG;
            break;
        case 'ToOrCcMe':
            mainMessage = loc(tomeIsEmpty);
            icon = EMPTY_TOME_SVG;
            break;
        case 'Mentioned':
            mainMessage = loc(mentionIsEmpty);
            icon = EMPTY_MENTIONS_SVG;
            break;
        case 'HasAttachment':
            mainMessage = loc(attachmentsIsEmpty);
            detailMessage = loc(attachmentsIsEmptyDetailMessage);
            icon = EMPTY_ATTACHMENTS_SVG;
            break;

        // Do not display clear filter command for 'UserCategory' view filter
        case 'UserCategory':
            mainMessage = loc(categoryFolderEmpty);
            ctaMessage = null;
            onCtaClick = null;
            break;
    }

    emptyStateProps.mainMessage = mainMessage;
    emptyStateProps.detailMessage = detailMessage;
    emptyStateProps.ctaMessage = ctaMessage;
    emptyStateProps.onCtaClick = onCtaClick;
    emptyStateProps.icon = icon;
}

/**
 * initialize the svgs icons
 */
function initializeSvgsForTheme() {
    const isLightBaseTheme = getIsLightBaseTheme();

    if (isFeatureEnabled('tri-emptyStateUpdates')) {
        if (getIsMsHighContrast()) {
            EMPTY_INBOX_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_balloon.svg');
            EMPTY_OTHER_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_drink.svg');
            EMPTY_FOLDER_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_folder.svg');
            EMPTY_DRAFT_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_draft.svg');
            EMPTY_SENT_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_sent.svg');
            EMPTY_ARCHIVE_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_archive.svg');
            EMPTY_DELETED_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_trash.svg');
            EMPTY_JUNK_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_junk.svg');
            EMPTY_MENTIONS_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_mention.svg');
            EMPTY_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_attachment.svg');
            EMPTY_FLAGGED_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_flagged.svg');
            EMPTY_TOME_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_to_me.svg');
            EMPTY_UNREAD_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_inbox.svg');
            EMPTY_SEARCH_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_search.svg');
            EMPTY_SEARCH_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_search_file.svg');
            EMPTY_SEARCH_CUSTOM_FOLDER_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_search_folder.svg');
            LOADING_ERROR_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_error.svg');
            ACCESS_ERROR_SVG = require('owa-empty-state/lib/svg/HighContrast/illustration_access_error.svg');
        } else if (isLightBaseTheme) {
            EMPTY_INBOX_SVG = require('owa-empty-state/lib/svg/Light/illustration_balloon.svg');
            EMPTY_OTHER_SVG = require('owa-empty-state/lib/svg/Light/illustration_drink.svg');
            EMPTY_FOLDER_SVG = require('owa-empty-state/lib/svg/Light/illustration_folder.svg');
            EMPTY_DRAFT_SVG = require('owa-empty-state/lib/svg/Light/illustration_draft.svg');
            EMPTY_SENT_SVG = require('owa-empty-state/lib/svg/Light/illustration_sent.svg');
            EMPTY_ARCHIVE_SVG = require('owa-empty-state/lib/svg/Light/illustration_archive.svg');
            EMPTY_DELETED_SVG = require('owa-empty-state/lib/svg/Light/illustration_trash.svg');
            EMPTY_JUNK_SVG = require('owa-empty-state/lib/svg/Light/illustration_junk.svg');
            EMPTY_MENTIONS_SVG = require('owa-empty-state/lib/svg/Light/illustration_mention.svg');
            EMPTY_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/Light/illustration_attachment.svg');
            EMPTY_FLAGGED_SVG = require('owa-empty-state/lib/svg/Light/illustration_flagged.svg');
            EMPTY_TOME_SVG = require('owa-empty-state/lib/svg/Light/illustration_to_me.svg');
            EMPTY_UNREAD_SVG = require('owa-empty-state/lib/svg/Light/illustration_inbox.svg');
            EMPTY_SEARCH_SVG = require('owa-empty-state/lib/svg/Light/illustration_search.svg');
            EMPTY_SEARCH_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/Light/illustration_search_file.svg');
            EMPTY_SEARCH_CUSTOM_FOLDER_SVG = require('owa-empty-state/lib/svg/Light/illustration_search_folder.svg');
            LOADING_ERROR_SVG = require('owa-empty-state/lib/svg/Light/illustration_error.svg');
            ACCESS_ERROR_SVG = require('owa-empty-state/lib/svg/Light/illustration_access_error.svg');
        } else if (getIsDarkBaseTheme()) {
            EMPTY_INBOX_SVG = require('owa-empty-state/lib/svg/Dark/illustration_balloon.svg');
            EMPTY_OTHER_SVG = require('owa-empty-state/lib/svg/Dark/illustration_drink.svg');
            EMPTY_FOLDER_SVG = require('owa-empty-state/lib/svg/Dark/illustration_folder.svg');
            EMPTY_DRAFT_SVG = require('owa-empty-state/lib/svg/Dark/illustration_draft.svg');
            EMPTY_SENT_SVG = require('owa-empty-state/lib/svg/Dark/illustration_sent.svg');
            EMPTY_ARCHIVE_SVG = require('owa-empty-state/lib/svg/Dark/illustration_archive.svg');
            EMPTY_DELETED_SVG = require('owa-empty-state/lib/svg/Dark/illustration_trash.svg');
            EMPTY_JUNK_SVG = require('owa-empty-state/lib/svg/Dark/illustration_junk.svg');
            EMPTY_MENTIONS_SVG = require('owa-empty-state/lib/svg/Dark/illustration_mention.svg');
            EMPTY_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/Dark/illustration_attachment.svg');
            EMPTY_FLAGGED_SVG = require('owa-empty-state/lib/svg/Dark/illustration_flagged.svg');
            EMPTY_TOME_SVG = require('owa-empty-state/lib/svg/Dark/illustration_to_me.svg');
            EMPTY_UNREAD_SVG = require('owa-empty-state/lib/svg/Dark/illustration_inbox.svg');
            EMPTY_SEARCH_SVG = require('owa-empty-state/lib/svg/Dark/illustration_search.svg');
            EMPTY_SEARCH_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/Dark/illustration_search_file.svg');
            EMPTY_SEARCH_CUSTOM_FOLDER_SVG = require('owa-empty-state/lib/svg/Dark/illustration_search_folder.svg');
            LOADING_ERROR_SVG = require('owa-empty-state/lib/svg/Dark/illustration_error.svg');
            ACCESS_ERROR_SVG = require('owa-empty-state/lib/svg/Dark/illustration_access_error.svg');
        } else if (getIsDarkTheme()) {
            EMPTY_INBOX_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_balloon.svg');
            EMPTY_OTHER_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_drink.svg');
            EMPTY_FOLDER_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_folder.svg');
            EMPTY_DRAFT_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_draft.svg');
            EMPTY_SENT_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_sent.svg');
            EMPTY_ARCHIVE_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_archive.svg');
            EMPTY_DELETED_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_trash.svg');
            EMPTY_JUNK_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_junk.svg');
            EMPTY_MENTIONS_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_mention.svg');
            EMPTY_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_attachment.svg');
            EMPTY_FLAGGED_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_flagged.svg');
            EMPTY_TOME_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_to_me.svg');
            EMPTY_UNREAD_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_inbox.svg');
            EMPTY_SEARCH_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_search.svg');
            EMPTY_SEARCH_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_search_file.svg');
            EMPTY_SEARCH_CUSTOM_FOLDER_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_search_folder.svg');
            LOADING_ERROR_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_error.svg');
            ACCESS_ERROR_SVG = require('owa-empty-state/lib/svg/DarkTheme/illustration_access_error.svg');
        } else {
            //colorful theme grayscale svgs
            EMPTY_INBOX_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_balloon.svg');
            EMPTY_OTHER_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_drink.svg');
            EMPTY_FOLDER_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_folder.svg');
            EMPTY_DRAFT_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_draft.svg');
            EMPTY_SENT_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_sent.svg');
            EMPTY_ARCHIVE_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_archive.svg');
            EMPTY_DELETED_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_trash.svg');
            EMPTY_JUNK_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_junk.svg');
            EMPTY_MENTIONS_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_mention.svg');
            EMPTY_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_attachment.svg');
            EMPTY_FLAGGED_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_flagged.svg');
            EMPTY_TOME_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_to_me.svg');
            EMPTY_UNREAD_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_inbox.svg');
            EMPTY_SEARCH_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_search.svg');
            EMPTY_SEARCH_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_search_file.svg');
            EMPTY_SEARCH_CUSTOM_FOLDER_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_search_folder.svg');
            LOADING_ERROR_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_error.svg');
            ACCESS_ERROR_SVG = require('owa-empty-state/lib/svg/LightTheme/illustration_access_error.svg');
        }
    } else {
        if (isLightBaseTheme) {
            // colored svgs that goes with the blue base theme
            EMPTY_INBOX_SVG = require('owa-empty-state/lib/svg/colored/emptyInbox.svg');
            EMPTY_FLAGGED_SVG = require('owa-empty-state/lib/svg/colored/emptyFlagged.svg');
            EMPTY_TOME_SVG = require('owa-empty-state/lib/svg/colored/emptyToMe.svg');
            EMPTY_UNREAD_SVG = require('owa-empty-state/lib/svg/colored/emptyUnread.svg');
            EMPTY_MENTIONS_SVG = require('owa-empty-state/lib/svg/colored/emptyMentions.svg');
            EMPTY_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/colored/emptyAttachments.svg');
            EMPTY_SEARCH_SVG = require('owa-empty-state/lib/svg/colored/emptySearch.svg');
            EMPTY_JUNK_SVG = require('owa-empty-state/lib/svg/colored/emptyJunkFolder.svg');
            EMPTY_DRAFT_SVG = require('owa-empty-state/lib/svg/colored/emptyDraftFolder.svg');
            EMPTY_DELETED_SVG = require('owa-empty-state/lib/svg/colored/emptyDeletedItemsFolder.svg');
            EMPTY_FOLDER_SVG = require('owa-empty-state/lib/svg/colored/emptyFolder.svg');
            LOADING_ERROR_SVG = require('owa-empty-state/lib/svg/colored/loadingError.svg');
            ACCESS_ERROR_SVG = require('owa-empty-state/lib/svg/colored/accessError.svg');
        } else {
            // gray scale svgs
            EMPTY_INBOX_SVG = require('owa-empty-state/lib/svg/gray/emptyInbox.svg');
            EMPTY_FLAGGED_SVG = require('owa-empty-state/lib/svg/gray/emptyFlagged.svg');
            EMPTY_TOME_SVG = require('owa-empty-state/lib/svg/gray/emptyToMe.svg');
            EMPTY_UNREAD_SVG = require('owa-empty-state/lib/svg/gray/emptyUnread.svg');
            EMPTY_MENTIONS_SVG = require('owa-empty-state/lib/svg/gray/emptyMentions.svg');
            EMPTY_ATTACHMENTS_SVG = require('owa-empty-state/lib/svg/gray/emptyAttachments.svg');
            EMPTY_SEARCH_SVG = require('owa-empty-state/lib/svg/gray/emptySearch.svg');
            EMPTY_JUNK_SVG = require('owa-empty-state/lib/svg/gray/emptyJunkFolder.svg');
            EMPTY_DRAFT_SVG = require('owa-empty-state/lib/svg/gray/emptyDraftFolder.svg');
            EMPTY_DELETED_SVG = require('owa-empty-state/lib/svg/gray/emptyDeletedItemsFolder.svg');
            EMPTY_FOLDER_SVG = require('owa-empty-state/lib/svg/gray/emptyFolder.svg');
            LOADING_ERROR_SVG = require('owa-empty-state/lib/svg/gray/loadingError.svg');
            ACCESS_ERROR_SVG = require('owa-empty-state/lib/svg/gray/accessError.svg');
        }
    }
}

function clearSpotlightFilter() {
    lazyClearSpotlightFilter.importAndExecute('EmptyListState');
}
